import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HeartHandshake, ArrowRight } from "lucide-react";
import { crowdfundingApi, type PublicCampaignItem } from "@/lib/api";

const fmtAmount = (v: string | number, currency: string) => {
  const n = Number(v);
  return `${currency === "KES" ? "Kshs" : currency} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const FeaturedCampaigns = () => {
  const [campaigns, setCampaigns] = useState<PublicCampaignItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // API already orders featured campaigns first.
        const all = await crowdfundingApi.listPublic();
        if (alive) setCampaigns(all.filter((c) => c.status === "active").slice(0, 4));
      } catch {
        /* ignore — section just stays empty */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!loading && campaigns.length === 0) return null;

  return (
    <section className="py-12 bg-background">
      <div className="container mx-auto px-4">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <span className="text-[11px] font-medium tracking-wider text-gold mb-2 block">
              GIVE BACK, GROW TOGETHER
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-black leading-snug">
              Community Impact &amp; Crowdfunding
            </h2>
            <p className="text-sm text-black/70 mt-2 max-w-lg">
              Back verified campaigns from the AAMECC community — every contribution moves a business, a family or a cause forward.
            </p>
          </div>
          <Link
            to="/crowdfunding"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:underline shrink-0"
          >
            Explore all campaigns <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-gray-100" />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-2 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {campaigns.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: Math.min(i * 0.06, 0.4), ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-gold/40 transition-[box-shadow,border-color]"
              >
                <Link to={`/crowdfunding/${c.slug}`} className="block">
                  <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
                    {c.featured_image_url ? (
                      <img
                        src={c.featured_image_url}
                        alt={c.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-300">
                        <HeartHandshake size={40} />
                      </div>
                    )}
                    <span className="absolute top-2 left-2 bg-white/90 backdrop-blur text-navy text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
                      {c.category_display}
                    </span>
                  </div>

                  <div className="p-4">
                    <h3 className="text-[14px] font-bold text-black line-clamp-1 group-hover:text-gold transition-colors">
                      {c.title}
                    </h3>
                    {c.short_description && (
                      <p className="text-[12px] text-gray-500 mt-1 line-clamp-2 min-h-[2.4em]">
                        {c.short_description}
                      </p>
                    )}

                    <div className="flex items-center justify-between mt-3 gap-2">
                      <span className="text-[13px] font-bold text-black truncate">
                        {fmtAmount(c.amount_raised, c.currency)}
                        <span className="text-[11px] font-medium text-gray-400"> raised</span>
                      </span>
                      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-navy group-hover:text-gold transition-colors shrink-0">
                        Donate now <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>

                    <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full bg-gold rounded-full"
                        style={{ width: `${Math.min(c.progress_percent, 100)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1.5">
                      {Math.round(c.progress_percent)}% of {fmtAmount(c.target_amount, c.currency)} · {c.donors_count} donor{c.donors_count === 1 ? "" : "s"}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-8 text-center sm:hidden">
          <Link
            to="/crowdfunding"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:underline"
          >
            Explore all campaigns <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedCampaigns;
